<template>
  <div class="membership-card-edit-mode-section">
    <switch-edit-mode-button-parts
      class="membership-card-edit-mode-section__cancel-button"
      type="cancel"
      @click="emitCancel"
    />

    <button
      class="membership-card-edit-mode-section__save-button"
      v-text="$tc('common.save')"
      @click="handlerSubmit()"
    />

    <div class="membership-card-edit-mode-section__image">
      <figure class="membership-card-edit-mode-section__image-thumbnail">
        <img
          :src="
            userIconTmpUrl
              ? userIconTmpUrl
              : userPreviewData.image || require('@/assets/img/icon/icon_user_default.png')
          "
          alt=""
        />
      </figure>
      <label class="membership-card-edit-mode-section__image-select">
        <input type="file" ref="imageInput" accept="image/*" @change="uploadFile" />
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.666 5.33203V3.9987H11.3327V2.66536H12.666V1.33203H13.9994V2.66536H15.3327V3.9987H13.9994V5.33203H12.666ZM1.99935 14.6654C1.63268 14.6654 1.31868 14.5347 1.05735 14.2734C0.796017 14.012 0.665572 13.6983 0.666017 13.332V5.33203C0.666017 4.96536 0.796683 4.65136 1.05802 4.39003C1.31935 4.1287 1.63313 3.99825 1.99935 3.9987H4.09935L5.33268 2.66536H9.33269V3.9987H5.91602L4.69935 5.33203H1.99935V13.332H12.666V7.33203H13.9994V13.332C13.9994 13.6987 13.8687 14.0127 13.6074 14.274C13.346 14.5354 13.0322 14.6658 12.666 14.6654H1.99935ZM7.33269 12.332C8.16602 12.332 8.87446 12.0403 9.45802 11.4567C10.0416 10.8731 10.3331 10.1649 10.3327 9.33203C10.3327 8.4987 10.0409 7.79025 9.45735 7.2067C8.8738 6.62314 8.16557 6.33159 7.33269 6.33203C6.49935 6.33203 5.79091 6.62381 5.20735 7.20736C4.6238 7.79092 4.33224 8.49914 4.33268 9.33203C4.33268 10.1654 4.62446 10.8738 5.20802 11.4574C5.79157 12.0409 6.4998 12.3325 7.33269 12.332ZM7.33269 10.9987C6.86602 10.9987 6.47157 10.8376 6.14935 10.5154C5.82713 10.1931 5.66602 9.7987 5.66602 9.33203C5.66602 8.86536 5.82713 8.47092 6.14935 8.1487C6.47157 7.82648 6.86602 7.66536 7.33269 7.66536C7.79935 7.66536 8.1938 7.82648 8.51602 8.1487C8.83824 8.47092 8.99935 8.86536 8.99935 9.33203C8.99935 9.7987 8.83824 10.1931 8.51602 10.5154C8.1938 10.8376 7.79935 10.9987 7.33269 10.9987Z"
            fill="white"
            fill-opacity="0.7"
          />
        </svg>
      </label>
    </div>

    <div class="membership-card-edit-mode-section__name">
      <atom-input-text
        class="membership-card-edit-mode-section__name-input"
        :placeholder="$tc('formParts.placeholder.displayName')"
        v-model="previewName"
        :error="isEditCardForHighlightComment && !userData.name"
      />
      <div
        v-if="isEditCardForHighlightComment && !userData.name"
        class="membership-card-edit-mode-section__name__error"
      >
        <p class="membership-card-edit-mode-section__name__error-message">
          {{
            isTransitionCreateHighlight
              ? $tc('MypagePage.errorMessage.displayNameForHighlight')
              : $tc('MypagePage.errorMessage.displayNameForComment')
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import { UserDataType } from '@/components/MypageTopPage/MembershipCardPane.vue'
import SwitchEditModeButtonParts from '@/components/MypageTopPage/MembershipCardPane/parts/SwitchEditModeButtonParts.vue'
import AtomInputText from '@/components/atoms/input/AtomInputText.vue'
import MessageDialogStore from '@/store/stores/pageStore/common/MessageDialogStore'
import I18n from '@/locales/I18n'
import Const from '@/util/Const'

/**
 * マイページ: 会員証 編集モードセクション
 */
export default defineComponent({
  name: 'MembershipCardEditModeSection',
  components: {
    SwitchEditModeButtonParts,
    AtomInputText,
  },
  props: {
    /**
     * 登録済みのユーザー情報
     */
    userData: {
      type: Object as PropType<UserDataType>,
      default: () => ({}),
      require: true,
    },
    userPreviewData: {
      type: Object as PropType<UserDataType>,
      default: () => ({}),
      require: true,
    },
    /**
     * 会員証を編集モードで表示するかどうか
     */
    isEditCardForHighlightComment: {
      type: Boolean,
      default: false,
    },
    /**
     * 遷移元が動画再生画面でハイライト作成でニックネームが未登録で遷移してきたかどうか
     */
    isTransitionCreateHighlight: {
      type: Boolean,
      default: false,
    },
    /**
     * アイコン画像データ
     */
    valueUserIconFileData: {
      type: File as PropType<File>,
      default: null,
    },
  },
  setup(props, context) {
    /** プレビュー用表示名 */
    const previewName = computed({
      get: () => props.userPreviewData.name,
      set: (val: string) => context.emit('onChangeUsername', val),
    })

    /** ユーザーのアイコン画像ファイル */
    const userIconFileData = computed(() => props.valueUserIconFileData)

    /** アップロードしたファイルの一時URL */
    const userIconTmpUrl = computed(() =>
      userIconFileData.value ? URL.createObjectURL(userIconFileData.value) : null,
    )

    return {
      previewName,
      userIconTmpUrl,
    }
  },
  methods: {
    /**
     * 編集キャンセル通知
     */
    async emitCancel() {
      this.$emit('handlerCancel')
    },
    /**
     * アップロード画像
     * プレビューセット処理
     */
    uploadFile() {
      const fileInput = this.$refs.imageInput as HTMLInputElement | null

      if (fileInput?.files) {
        this.$emit('onChangeUserIconData', fileInput.files[0])
        fileInput.value = ''
      }
    },
    /**
     * サブミット時の処理
     */
    async handlerSubmit() {
      if (this.previewName === '') {
        await MessageDialogStore.value.open({
          title: I18n.tc('MypagePage.errors.displayNameNull.title') as string,
          message: I18n.tc('MypagePage.errors.displayNameNull.message') as string,
        })
        return
      }
      if (this.previewName.length > Const.MAX_DISPLAY_NAME_LENGTH) {
        await MessageDialogStore.value.open({
          title: I18n.tc('MypagePage.errors.displayNameLengthExceeded.title') as string,
          message: I18n.tc('MypagePage.errors.displayNameLengthExceeded.message') as string,
        })
        return
      }

      this.$emit('handlerSubmit')
    },
  },
})
</script>

<style lang="scss" scoped>
.membership-card-edit-mode-section {
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  align-content: center;
  align-items: center;
  justify-content: center;
  justify-items: center;
  width: 100%;
  min-height: 218px;
  padding: 24px 10px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(14.5px);
  border-radius: 10px;

  &__save-button {
    position: absolute;
    right: min(#{convertPXtoVW(10)}, 10px);
    top: min(#{convertPXtoVW(10)}, 10px);
    display: flex;
    align-items: center;
    justify-content: center;
    width: auto;
    height: min(#{convertPXtoVW(32)}, 32px);
    padding: 0 min(#{convertPXtoVW(16)}, 16px);
    background: $black;
    cursor: pointer;
    border-radius: 60px;
    font-family: $fontSignup;
    font-weight: 700;
    font-size: min(#{convertPXtoVW(13)}, 13px);
    line-height: 1.54;
    letter-spacing: 0.04em;
    font-feature-settings: 'palt' on;
    color: $green-light;
  }

  &__image {
    position: relative;
    height: 64px;

    &-select {
      position: absolute;
      bottom: 0;
      left: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding-left: 1.5px;
      padding-bottom: 1px;
      margin-left: 24px;
      background: $black;
      border-radius: 50%;

      input {
        position: absolute;
        top: 0;
        left: 0;
        visibility: hidden;
      }
    }

    &-thumbnail {
      width: 64px;
      margin: 0 auto;

      img {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        border: 3px solid $white;
        overflow: hidden;
        object-fit: cover;
        object-position: center center;
      }
    }
  }

  &__name {
    position: relative;
    width: 100%;

    &-input {
      margin-top: 16px;
      width: calc(100% - 28px);
      color: $white;
      background: rgba(0, 0, 0, 0.55);
      backdrop-filter: blur(1px);
      border-radius: 4px;
      border: none;
      font-family: $fontSignup;
      font-weight: 500;
      font-size: 13px;
      line-height: 1.54;
      letter-spacing: 0.04em;
      font-feature-settings: 'palt' on;

      &:focus {
        color: $white;
        background: rgba(0, 0, 0, 0.55);
      }

      &:placeholder-shown {
        &::placeholder {
          color: $white50;
        }
      }

      &.input--validation-error {
        border: 1px solid rgba(#ff2e00, 0.5);
      }
    }

    &__error {
      position: absolute;
      top: 68px;
      left: 50%;
      width: calc(100% - 28px);
      text-align: left;
      font-size: 10px;
      font-weight: 500;
      line-height: 1.4;
      transform: translateX(-50%);

      &-message {
        display: inline-block;
        max-height: 10px * 1.4 * 2 + 4;
        padding: 2px 8px;
        overflow: hidden;
        background-color: $red-light;
        border-radius: 11px;
        font-size: 10px;
        font-weight: 500;
        color: $black;
        letter-spacing: 0.04em;
      }
    }
  }
}
</style>
